/**
 * TMS Trips Page — Trip listing and management
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRenderMode } from '@/hooks/useRenderMode';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, MapPin, Truck, ArrowRight, Loader2, Share2,
} from 'lucide-react';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { api } from '@/lib/api';
import { ComplianceField, ComplianceLegend } from '@/components/compliance';
import { ShareLinkDialog } from '@/components/entity/ShareLinkDialog';

interface Trip {
  id: string;
  trip_no: string;
  job_id: string;
  job_no: string;
  job_number?: string;
  status: string;
  driver_name: string;
  driver?: string;
  vehicle_no: string;
  vehicle_type?: string;
  trailer_no?: string;
  trailer_type?: string;
  vehicle?: string;
  pickup_address: string;
  delivery_address: string;
  pickup_date: string;
  delivery_date: string;
  created_at: string;
}

const statusColor: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-amber-100 text-amber-700',
  delivered: 'bg-green-100 text-green-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function TmsTripsPage() {
  const isMobile = useRenderMode() === 'mobile';
  const [trips, setTrips] = useState<Trip[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DataErrorDetail | null>(null);
  const [shareTrip, setShareTrip] = useState<Trip | null>(null);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('all');
  const [trailerTypeFilter, setTrailerTypeFilter] = useState('all');
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<string[]>([]);
  const [trailerTypeOptions, setTrailerTypeOptions] = useState<string[]>([]);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ items?: Trip[] } | Trip[]>('/job-trips?limit=100&job_type=LCL,LOCAL,LORRY_RENTAL');
      setTrips(Array.isArray(res) ? res : (res.items ?? []));
      setError(null);
    } catch {
      setError({ message: 'Failed to load trips' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  useEffect(() => {
    api.get<{ items?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>>('/tms/vehicles?limit=100')
      .then((res) => {
        const items = Array.isArray(res) ? res : (res.items ?? []);
        const vehicleTypes = new Set<string>();
        const trailerTypes = new Set<string>();
        for (const item of items) {
          const vehicleType = String(item.vehicle_type ?? item.type ?? '').trim();
          let data: Record<string, unknown> = {};
          try {
            data = typeof item.data === 'string' ? JSON.parse(item.data) : ((item.data as Record<string, unknown>) ?? {});
          } catch { data = {}; }
          const trailerType = String(data.trailer_type ?? data.trailerType ?? vehicleType).trim();
          if (vehicleType && !vehicleType.toUpperCase().includes('TRAILER')) vehicleTypes.add(vehicleType);
          if (trailerType && vehicleType.toUpperCase().includes('TRAILER')) trailerTypes.add(trailerType);
        }
        setVehicleTypeOptions(Array.from(vehicleTypes).sort());
        setTrailerTypeOptions(Array.from(trailerTypes).sort());
      })
      .catch(() => {
        setVehicleTypeOptions([]);
        setTrailerTypeOptions([]);
      });
  }, []);

  const filtered = useMemo(() => trips.filter((t) => {
    const q = search.toLowerCase();
    const vehicleType = String(t.vehicle_type ?? '').trim();
    const trailerType = String(t.trailer_type ?? '').trim();
    const jobNo = t.job_no || t.job_number || '';
    const driverName = t.driver_name || t.driver || '';
    if (vehicleTypeFilter !== 'all' && vehicleType !== vehicleTypeFilter) return false;
    if (trailerTypeFilter !== 'all' && trailerType !== trailerTypeFilter) return false;
    return !q || [t.trip_no, jobNo, driverName, t.vehicle_no, t.vehicle, t.vehicle_type, t.trailer_no, t.trailer_type, t.pickup_address, t.delivery_address]
      .some((v) => v?.toLowerCase().includes(q));
  }), [trips, search, vehicleTypeFilter, trailerTypeFilter]);

  return (
    <div className="space-y-4">
      {error && <DataErrorBanner error={error} entityLabel="Trips" onRetry={fetchTrips} />}

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search trips..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={vehicleTypeFilter} onValueChange={setVehicleTypeFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Vehicle type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicleTypeOptions.map((type) => <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={trailerTypeFilter} onValueChange={setTrailerTypeFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Trailer type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trailers</SelectItem>
            {trailerTypeOptions.map((type) => <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="secondary">{filtered.length} trips</Badge>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No trips found</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((trip) => (
            <Card key={trip.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="py-3 px-4">
                <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{trip.trip_no || `T-${trip.id.slice(0, 6).toUpperCase()}`}</span>
                        <Badge className={`text-[10px] ${statusColor[trip.status] ?? 'bg-gray-100'}`}>{trip.status}</Badge>
                      </div>
                      <div className="mt-0.5 font-mono text-xs text-primary">{trip.job_no || trip.job_number || '—'}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {trip.pickup_address || '—'} <ArrowRight className="h-3 w-3" /> {trip.delivery_address || '—'}
                      </div>
                    </div>
                  </div>
                  <ComplianceField className="p-1.5">
                    <div className={`flex ${isMobile ? 'flex-col items-start gap-2' : 'items-center gap-4'} text-xs text-muted-foreground`}>
                      <span>{trip.driver_name || trip.driver || '—'}</span>
                      <span>{trip.vehicle_no || trip.vehicle || '—'}{trip.vehicle_type ? ` · ${trip.vehicle_type}` : ''}{trip.trailer_type ? ` · ${trip.trailer_type}` : ''}</span>
                      <span>{trip.pickup_date ? new Date(trip.pickup_date).toLocaleDateString() : '—'}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-[11px]"
                        onClick={() => setShareTrip(trip)}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                        Share
                      </Button>
                    </div>
                  </ComplianceField>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ComplianceLegend compact />

      <ShareLinkDialog
        open={!!shareTrip}
        onOpenChange={(open) => {
          if (!open) setShareTrip(null);
        }}
        entitySlug="job-trips"
        entityId={shareTrip?.id ?? ''}
        recordTitle={shareTrip?.trip_no || shareTrip?.job_no || 'Trip'}
        record={shareTrip ? ({ ...shareTrip } as Record<string, unknown>) : undefined}
      />
    </div>
  );
}
